import { Color } from 'three';

/**
 * The instrument palette. Kept as sRGB hex so the same values can feed three
 * `Color`s, TSL uniforms and CSS without a round trip.
 */
export const HEX = {
  pureWhite: 0xffffff,
  white: 0xaaaaaa,
  red: 0xc35944,
  yellow: 0xe2b957,
  blue: 0x4b6bbe,
  lightblue: 0xaaffff,
  green: 0x36bcb2,
  grey: 0x272727,
  darkGrey: 0x101010,
  lightgrey: 0x505050,
  pink: 0xc3446e,
  teal: 0x44c3c3,
  black: 0x151530,
} as const;

export const COLORS = {
  pure_white: new Color(HEX.pureWhite),
  white: new Color(HEX.white),
  red: new Color(HEX.red),
  yellow: new Color(HEX.yellow),
  darkblue: new Color(HEX.blue).addScalar(-0.2),
  blue: new Color(HEX.blue),
  lightblue: new Color(HEX.lightblue),
  green: new Color(HEX.green),
  grey: new Color(HEX.grey),
  darkGrey: new Color(HEX.darkGrey),
  lightgrey: new Color(HEX.lightgrey),
  pink: new Color(HEX.pink),
  teal: new Color(HEX.teal),
  black: new Color(HEX.black),
} as const;

/**
 * How far above white the display and the lit overlays are driven.
 *
 * These surfaces are self-lit, so they need to land in HDR range to survive the
 * output transform and to cross the bloom threshold — that glow is what the
 * original got for free by having no tone curve at all. Raise for a hotter
 * panel, drop toward 1 for a flat one.
 */
export const EMISSIVE_GAIN = 2.6;

/**
 * Luminance above which the frame blooms. Above white, so only the self-lit
 * panel blooms: lit-but-not-emissive surfaces like the white keys top out at 1
 * and stay crisp.
 */
export const BLOOM_THRESHOLD = 1.15;

/** Names of the mesh groups inside `VSS_Final3b.glb`. */
export const CONTROL_GROUPS = {
  KEYBED: 'Keybed',
  DISPLAYS: 'Display',
  FADERS: 'FaderControls',
  CONTROLS: 'Controls',
} as const;

/** Local-space X range the fader caps travel along. */
export const FADER_LIMITS = { MIN: 3.466237783432007, MAX: 4.3462377834320005 } as const;

/** Radians a keybed key rotates when held. */
export const KEY_DOWN_ROTATION = 0.09;

/** Resting X rotation of the whole instrument. */
export const BASE_ROTATION = 1.4;

/** MIDI note the leftmost modelled key (`Key_0`) plays. */
export const KEYBED_ROOT_NOTE = 52;
